import { routes } from '../../../common/contants/Routes';
import { isGuideDisabled } from '../helpers/guideDisableHelper';
import {
  applyHostColorSchemeToIframeElement,
  getHostColorScheme,
  startHostColorSchemeObserver
} from '../helpers/hostColorSchemeHelper';
import { MessageTypes } from '../types/MessageTypes';

class ChecklistContainer {
  private static instance: ChecklistContainer | null = null;
  private container: HTMLIFrameElement | null = null;
  private companyId: string | null = null;
  private fadeOutTimeout: NodeJS.Timeout | null = null;
  private stopColorSchemeObserver: (() => void) | null = null;

  private constructor() {
    this.initialize();
  }

  public static getInstance(): ChecklistContainer {
    if (!ChecklistContainer.instance) {
      ChecklistContainer.instance = new ChecklistContainer();
    }
    return ChecklistContainer.instance;
  }

  private initialize(): void {
    const scriptElement = document.querySelector('script[gd-company-id]');
    this.companyId = scriptElement?.getAttribute('gd-company-id') || null;
    this.container = document.createElement('iframe');
    const baseUrl = import.meta.env.VITE_TOUR_GUIDE_FRONTEND_URL;
    const iframeUrl = new URL(`${baseUrl}${routes.CHECKLIST_PLAYER}/${this.companyId}`);

    const parentParams = new URLSearchParams(window.location.search);
    if (parentParams.get('guideLog') === 'true') {
      iframeUrl.searchParams.set('guideLog', 'true');
    }
    if (isGuideDisabled()) {
      iframeUrl.searchParams.set('guideDisable', 'true');
    }
    // Chrome force-paints an opaque canvas on the iframe whenever the iframe's
    // declared color-scheme doesn't match the host page's. Forwarding the host's
    // computed color-scheme (mirrored inside the iframe via ChecklistPlayer /
    // GuideProvider) keeps them aligned for every host, including app.getdemo.com.br
    // which doesn't declare color-scheme at all.
    const hostColorScheme = getHostColorScheme();
    iframeUrl.searchParams.set('hostColorScheme', hostColorScheme);

    this.container.src = iframeUrl.toString();
    applyHostColorSchemeToIframeElement(this.container, hostColorScheme);
    this.container.toggleAttribute('getdemo-checklist-player', true);
    this.container.style.setProperty('position', 'fixed');
    this.container.style.setProperty('border', 'none');
    this.container.style.setProperty('background-color', 'transparent');
    this.container.style.setProperty('z-index', '2147483646');
    this.container.style.setProperty('width', '0');
    this.container.style.setProperty('height', '0');
    this.container.style.setProperty('top', '0');
    this.container.style.setProperty('left', '0');
    this.container.style.setProperty('bottom', 'auto');
    this.container.style.setProperty('right', 'auto');
    this.container.style.setProperty('transform', 'none');
    this.container.style.setProperty('opacity', '0');
    this.container.style.setProperty('display', 'none');
    this.container.style.setProperty('overflow', 'hidden');

    this.setupListeners();
    this.stopColorSchemeObserver = startHostColorSchemeObserver(scheme =>
      this.syncHostColorScheme(scheme)
    );
    document.documentElement.appendChild(this.container);
  }

  private syncHostColorScheme(hostColorScheme: string): void {
    applyHostColorSchemeToIframeElement(this.container, hostColorScheme);
    this.sendMessage({
      type: MessageTypes.HOST_COLOR_SCHEME_CHANGED,
      hostColorScheme
    });
  }

  private setupListeners(): void {
    if (!this.container) return;

    this.container.addEventListener('load', () => {
      this.sendSavedUserData();
      this.syncHostColorScheme(getHostColorScheme());
    });
  }

  private sendSavedUserData(): void {
    const savedUserData = localStorage.getItem('getdemo_user');
    if (savedUserData) {
      try {
        const userData = JSON.parse(savedUserData);
        this.sendMessage({
          type: MessageTypes.IDENTIFY_USER,
          userData
        });
      } catch (error) {
        console.error('Error processing saved user data:', error);
      }
    }
  }

  public sendMessage(message: any): void {
    if (this.container?.contentWindow) {
      this.container.contentWindow.postMessage(message, '*');
    }
  }

  public getContainer(): HTMLIFrameElement | null {
    return this.container;
  }

  public resizeAndPosition(
    position: {
      top: string;
      left: string;
      bottom: string;
      right: string;
      transform: string;
    },
    dimensions: { width: string; height: string }
  ): void {
    if (this.container) {
      this.container.style.setProperty('top', position.top);
      this.container.style.setProperty('left', position.left);
      this.container.style.setProperty('bottom', position.bottom);
      this.container.style.setProperty('right', position.right);
      this.container.style.setProperty('transform', position.transform);
      this.container.style.setProperty('width', dimensions.width);
      this.container.style.setProperty('height', dimensions.height);
    }
  }

  public show(): void {
    if (!this.container) return;

    if (this.fadeOutTimeout) {
      clearTimeout(this.fadeOutTimeout);
      this.fadeOutTimeout = null;
    }

    const wasHidden =
      this.container.style.display === 'none' ||
      getComputedStyle(this.container).display === 'none';

    this.container.style.setProperty('display', 'block');

    if (!wasHidden) {
      this.container.style.setProperty('opacity', '1');
      return;
    }

    this.container.style.setProperty('opacity', '1');
  }

  public hide(): void {
    if (!this.container) return;

    if (this.fadeOutTimeout) {
      clearTimeout(this.fadeOutTimeout);
    }

    this.container.style.setProperty('opacity', '0');

    this.fadeOutTimeout = setTimeout(() => {
      if (this.container) {
        this.container.style.setProperty('display', 'none');
      }
      this.fadeOutTimeout = null;
    }, 500);
  }

  public destroy(): void {
    if (this.stopColorSchemeObserver) {
      this.stopColorSchemeObserver();
      this.stopColorSchemeObserver = null;
    }

    if (this.fadeOutTimeout) {
      clearTimeout(this.fadeOutTimeout);
      this.fadeOutTimeout = null;
    }

    if (this.container && document.body.contains(this.container)) {
      document.documentElement.removeChild(this.container);
      this.container = null;
    }
    ChecklistContainer.instance = null;
  }

  public static destroy(): void {
    if (ChecklistContainer.instance) {
      ChecklistContainer.instance.destroy();
    }
  }
}

export default ChecklistContainer;
